import { useFormik } from 'formik';
import { changePasswordSchema } from '@/utils/validations';
import { KeenIcon } from '@/components';
import { useState } from 'react';
import clsx from 'clsx';
import { useChangePasswordMutation } from '@/services/AuthService';
import {
  ChangePasswordRequest,
  ChangePasswordResponse,
} from '@/types/auth.types';
import { preventLeadingWhitespace } from '@/utils/helperFunctions';
import { enqueueSnackbar } from 'notistack';
import { ErrorApiResponse } from '@/types/api.types';

const ChangePassword = () => {
  const [changePassword, { isLoading: loading }] = useChangePasswordMutation();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPasswordConfirmation, setShowNewPasswordConfirmation] =
    useState(false);

  const handleChangePassword = async (
    values: ChangePasswordRequest,
    setSubmitting: (isSubmitting: boolean) => void
  ): Promise<void> => {
    try {
      const response: ChangePasswordResponse = await changePassword({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      }).unwrap();
      enqueueSnackbar(response.message || 'Password changed successfully', {
        variant: 'solid',
        state: 'success',
      });
    } catch (error) {
      enqueueSnackbar(
        (error as ErrorApiResponse).data.message || 'An error occurred',
        {
          variant: 'solid',
          state: 'danger',
        }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: changePasswordSchema,
    onSubmit: async (values, { setSubmitting }) => {
      await handleChangePassword(values, setSubmitting);
    },
  });

  return (
    <div className="w-full flex justify-center">
      <div className="card max-w-[370px] w-full">
        <h1 className="sr-only" id="change-password-page-heading">
          Change Password
        </h1>
        <form
          className="card-body flex flex-col gap-5 p-10"
          onSubmit={formik.handleSubmit}
          noValidate
          aria-labelledby="change-password-heading"
        >
          <div className="text-center">
            <h2
              id="change-password-heading"
              className="text-lg font-medium text-gray-900"
              tabIndex={-1}
            >
              Change Your Password
            </h2>
            <p className="text-sm text-gray-600">
              Enter your current and new passwords to update your account
              password
            </p>
          </div>

          <div className="flex flex-col gap-1">
            <label
              htmlFor="old-password"
              className="form-label text-gray-900 font-medium"
              aria-label="Current Password"
            >
              Current Password
            </label>
            <label className="input relative" aria-label="Current Password">
              <input
                id="old-password"
                type={showOldPassword ? 'text' : 'password'}
                placeholder="Enter your current password"
                autoComplete="current-password"
                {...formik.getFieldProps('oldPassword')}
                className={clsx('form-control bg-transparent pr-10', {
                  'is-invalid':
                    formik.touched.oldPassword && formik.errors.oldPassword,
                  'is-valid':
                    formik.touched.oldPassword && !formik.errors.oldPassword,
                })}
                onKeyDown={preventLeadingWhitespace}
                aria-invalid={
                  formik.touched.oldPassword && formik.errors.oldPassword
                    ? 'true'
                    : 'false'
                }
                aria-describedby={
                  formik.touched.oldPassword && formik.errors.oldPassword
                    ? 'old-password-error'
                    : undefined
                }
              />
              <button
                type="button"
                className="btn btn-icon absolute right-2 top-1/2 -translate-y-1/2"
                onClick={() => setShowOldPassword(!showOldPassword)}
                aria-label={
                  showOldPassword
                    ? 'Hide current password'
                    : 'Show current password'
                }
              >
                <KeenIcon
                  icon={showOldPassword ? 'eye-slash' : 'eye'}
                  className="text-gray-500"
                />
              </button>
            </label>
            {formik.touched.oldPassword && formik.errors.oldPassword && (
              <span
                id="old-password-error"
                role="alert"
                className="text-danger contrast:text-red-500 text-xs mt-1"
              >
                {formik.errors.oldPassword}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label
              htmlFor="new-password"
              className="form-label text-gray-900 font-medium"
              aria-label="New Password"
            >
              New Password
            </label>
            <label className="input relative" aria-label="New Password">
              <input
                id="new-password"
                type={showNewPassword ? 'text' : 'password'}
                placeholder="Enter your new password"
                autoComplete="new-password"
                {...formik.getFieldProps('newPassword')}
                className={clsx('form-control bg-transparent pr-10', {
                  'is-invalid':
                    formik.touched.newPassword && formik.errors.newPassword,
                  'is-valid':
                    formik.touched.newPassword && !formik.errors.newPassword,
                })}
                onKeyDown={preventLeadingWhitespace}
                aria-label="New Password"
                aria-invalid={
                  formik.touched.newPassword && formik.errors.newPassword
                    ? 'true'
                    : 'false'
                }
                aria-describedby={
                  formik.touched.newPassword && formik.errors.newPassword
                    ? 'new-password-error'
                    : undefined
                }
              />
              <button
                type="button"
                className="btn btn-icon absolute right-2 top-1/2 -translate-y-1/2"
                onClick={() => setShowNewPassword(!showNewPassword)}
                aria-label={
                  showNewPassword ? 'Hide new password' : 'Show new password'
                }
              >
                <KeenIcon
                  icon={showNewPassword ? 'eye-slash' : 'eye'}
                  className="text-gray-500"
                />
              </button>
            </label>
            {formik.touched.newPassword && formik.errors.newPassword && (
              <span
                id="new-password-error"
                role="alert"
                className="text-danger contrast:text-red-500 text-xs mt-1"
              >
                {formik.errors.newPassword}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label
              htmlFor="confirm-password"
              className="form-label text-gray-900 font-medium"
              aria-label="Confirm New Password"
            >
              Confirm New Password
            </label>
            <label className="input relative" aria-label="Confirm New Password">
              <input
                id="confirm-password"
                type={showNewPasswordConfirmation ? 'text' : 'password'}
                placeholder="Confirm your new password"
                autoComplete="new-password"
                {...formik.getFieldProps('confirmPassword')}
                className={clsx('form-control bg-transparent pr-10', {
                  'is-invalid':
                    formik.touched.confirmPassword &&
                    formik.errors.confirmPassword,
                  'is-valid':
                    formik.touched.confirmPassword &&
                    !formik.errors.confirmPassword,
                })}
                onKeyDown={preventLeadingWhitespace}
                aria-invalid={
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                    ? 'true'
                    : 'false'
                }
                aria-label="Confirm New Password"
                aria-describedby={
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                    ? 'confirm-password-error'
                    : undefined
                }
              />
              <button
                type="button"
                className="btn btn-icon absolute right-2 top-1/2 -translate-y-1/2"
                onClick={() =>
                  setShowNewPasswordConfirmation(!showNewPasswordConfirmation)
                }
                aria-label={
                  showNewPasswordConfirmation
                    ? 'Hide confirm password'
                    : 'Show confirm password'
                }
              >
                <KeenIcon
                  icon={showNewPasswordConfirmation ? 'eye-slash' : 'eye'}
                  className="text-gray-500"
                />
              </button>
            </label>
            {formik.touched.confirmPassword &&
              formik.errors.confirmPassword && (
                <span
                  id="confirm-password-error"
                  role="alert"
                  className="text-danger contrast:text-red-500 text-xs mt-1"
                >
                  {formik.errors.confirmPassword}
                </span>
              )}
          </div>

          <button
            type="submit"
            className="btn btn-primary flex justify-center grow"
            disabled={loading || formik.isSubmitting}
            aria-disabled={loading || formik.isSubmitting}
          >
            {loading || formik.isSubmitting
              ? 'Please wait...'
              : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export { ChangePassword };
